import { Search as SearchIcon } from '@mui/icons-material';
import { Button, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { AppDrawer, CleverServiceLogo } from 'cleveron-web-components';
import React, { forwardRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { bindActionCreators } from 'redux';

import { toggleDarkMode as toggleDarkModeAction } from '../../actions/darkMode';
import { pathChanged as pathChangedAction } from '../../actions/location';
import { closeMenu as closeMenuAction, openMenu as openMenuAction } from '../../actions/menu';
import { closeSnackbar as closeSnackbarAction } from '../../actions/snackbar';
import { SEARCH_PATHS } from '../../constants';
import AccountMenu from '../AccountMenu';
import LocaleSelector from '../LocaleSelector';
import MainMenu from '../MainMenu';
import CleverServiceBackground from '../MainMenu/cleverservice-bg.png';

const useStyles = makeStyles(theme => ({
    button: {
        '&:hover': {
            backgroundColor: theme.isDark() ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
        },
        color: theme.palette.text.primary,
        position: 'relative',
        top: 1,
    },
    text: {
        fontSize: 13,
        fontWeight: 500,
        paddingLeft: 10,
        paddingTop: 2,
    },
}));

function SearchButton({ id, pathname }) {
    const classes = useStyles();

    const [, searchPath] =
        Object.entries(SEARCH_PATHS).find(([prefix]) => pathname.startsWith(prefix)) || [];

    if (searchPath) {
        return (
            <Button className={classes.button} component={Link} id={id} to={searchPath}>
                <SearchIcon />
                <Typography className={classes.text}>SEARCH</Typography>
            </Button>
        );
    }

    return null;
}

function ConnectedAppDrawer({
    darkMode,
    collapsed,
    closeMenu,
    closeSnackbar,
    hideMainMenu,
    isAccountMenuOpen,
    id,
    isMainMenuOpen,
    openMenu,
    pathChanged,
    snackbar,
    toggleDarkMode,
    toggleCollapsed,
    version,
}) {
    const { pathname } = useLocation();

    useEffect(() => {
        pathChanged(pathname);
    }, [pathname, pathChanged]);

    return (
        <AppDrawer
            /* eslint-disable-next-line react/display-name */
            AccountMenu={forwardRef((props, ref) => (
                <AccountMenu {...props} {...ref} closeMenu={closeMenu} />
            ))}
            Logo={CleverServiceLogo}
            MainMenu={MainMenu}
            backgroundImage={CleverServiceBackground}
            closeAccountMenu={() => closeMenu('account')}
            closeMainMenu={() => closeMenu('main')}
            closeSnackbar={closeSnackbar}
            collapsed={collapsed}
            darkMode={darkMode}
            hideMainMenu={hideMainMenu}
            id={id}
            isAccountMenuOpen={isAccountMenuOpen}
            isMainMenuOpen={isMainMenuOpen}
            isSnackbarOpen={snackbar.open}
            openAccountMenu={() => openMenu('account')}
            openMainMenu={() => openMenu('main')}
            snackbarProps={{ type: 'info', ...snackbar }}
            toggleCollapsed={toggleCollapsed}
            toggleDarkMode={toggleDarkMode}
            toolbarElements={
                <>
                    <SearchButton id={`${id}SearchButton`} pathname={pathname} />
                    <LocaleSelector />
                </>
            }
            version={version}
        />
    );
}

function mapStateToProps({ darkMode, menu, snackbar, version }) {
    return {
        darkMode,
        isAccountMenuOpen: menu.account,
        isMainMenuOpen: menu.main,
        snackbar,
        version,
    };
}

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            closeMenu: closeMenuAction,
            closeSnackbar: closeSnackbarAction,
            openMenu: openMenuAction,
            pathChanged: pathChangedAction,
            toggleDarkMode: toggleDarkModeAction,
        },
        dispatch
    );

export default connect(mapStateToProps, mapDispatchToProps)(ConnectedAppDrawer);
